import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BrandingService,
  LoginService,
  MerchantBrandingAndDetails,
  TrackingInfo,
  TrackingService,
} from '@evermile/customer-tracking';
import { AuthService } from '../../services/auth.service';
import * as Sentry from '@sentry/angular-ivy';
import {
  CommunicationService,
  EditOrderService,
  GtmTracking,
  TrackingFinalStatuses,
  TrackingLiveStatuses,
} from 'common-ui';
import { environment } from '../../../environments/environment';
import { last, Subscription } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BreakpointObserver } from '@angular/cdk/layout';
import { EvermileChatAPI } from '@evermile/chat-wrapper-types';
import { ChannelSourceType } from '@evermile/inbox-service';
import TokenTypeEnum = TrackingInfo.TokenTypeEnum;

@Component({
  selector: 'ect-tracking-page',
  templateUrl: './tracking-page.component.html',
  styleUrls: ['./tracking-page.component.scss'],
})
export class TrackingPageComponent implements OnInit, OnDestroy {
  orderTrackingInfo!: TrackingInfo;
  brandingAndDetails!: MerchantBrandingAndDetails;
  loadingState = true;
  errorState = false;
  private trackingInterval?: ReturnType<typeof setInterval>;
  environment = environment;

  //Subscriptions
  updateOrderSub?: Subscription;
  openChatSub?: Subscription;

  private hideChat: boolean = false;
  private extraBottomPadding = 0;
  isAdmin?: boolean;
  subMobileMode?: Subscription;
  mobileMode = false;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    private communicationService: CommunicationService,
    private loginService: LoginService,
    private trackingService: TrackingService,
    private brandingService: BrandingService,
    private editOrderService: EditOrderService,
    private sanitizer: DomSanitizer,
    private breakpointObserver: BreakpointObserver,
    private renderer: Renderer2,
    private gtmTracking: GtmTracking,
  ) {}

  ngOnInit(): void {
    const token = this.getTokenFromUrl();
    if (token) {
      this.login(token);
    }
    this.updateOrderSub = this.editOrderService.orderWasUpdated$.subscribe(() => {
      this.getOrderTrackingInfo();
    });
    this.openChatSub = this.communicationService.openChat$.subscribe(() => {
      window.$evermileChat?.openChat?.();
    });
    this.subMobileMode = this.breakpointObserver.observe(['(max-width: 1024px)']).subscribe((result) => {
      this.mobileMode = result.matches;
    });

    const scriptTag = this.renderer.createElement('script');
    scriptTag.async = 1;
    scriptTag.src = environment.chatScriptUrl;
    this.renderer.appendChild(document.head, scriptTag);
  }

  private initializeChat() {
    if ((window.$evermileChat as EvermileChatAPI) && this.authService.getIdToken() && this.isAdmin !== undefined) {
      if (this.isAdmin) return;

      window.$evermileChat.init?.('evermile-inner', 'evermile-inner', this.orderTrackingInfo?.customerId);

      // inner undocumented function
      if ((window as any).$evermileChat.initializeWithToken) {
        (window as any).$evermileChat.initializeWithToken(
          this.authService.getIdToken(),
          ChannelSourceType.TrackingLink,
          this.orderTrackingInfo?.customerId,
        );
        (window.$evermileChat as any)?.setExternalStyles?.(
          {
            zIndex: 999,
            extraBottomPadding: this.extraBottomPadding,
            visibility: this.hideChat ? 'hidden' : 'visible',
          },
          this.orderTrackingInfo.orderId,
        );
      }
      return;
    }
    setTimeout(() => this.initializeChat(), 500);
  }

  onPromotionFabDisplayChange(e: 'invisible' | 'minimized-large' | 'minimized-small' | 'open') {
    if (e === 'open') {
      this.hideChat = true;
      return;
    }

    this.hideChat = false;
    if (e === 'minimized-large') {
      this.extraBottomPadding = 105;
    } else if (e === 'minimized-small') {
      this.extraBottomPadding += 88;
    } else {
      this.extraBottomPadding = 0;
    }

    (window.$evermileChat as any)?.setExternalStyles?.({
      extraBottomPadding: this.extraBottomPadding,
      visibility: this.hideChat ? 'hidden' : 'visible',
    });
  }

  ngOnDestroy() {
    clearInterval(this.trackingInterval);
    this.updateOrderSub?.unsubscribe();
    this.openChatSub?.unsubscribe();
  }

  private getTokenFromUrl() {
    return this.route.snapshot.paramMap.get('token');
  }

  login(trackingToken: string) {
    this.loginService
      .loginPost({ token: trackingToken })
      .pipe(last())
      .subscribe({
        next: (idToken) => {
          this.authService.setIdToken(idToken);
          this.getOrderTrackingInfo();
          this.getBrandingInfo();
        },
        error: (err) => {
          this.errorState = true;
          this.loadingState = false;
          Sentry.captureException(err);
        },
      });
  }

  getOrderTrackingInfo() {
    this.trackingService
      .trackingGet()
      .pipe(last())
      .subscribe({
        next: (trackingInfo) => {
          if (this.handleReRoute(trackingInfo)) {
            return;
          }
          this.orderTrackingInfo = trackingInfo;
          Sentry.setUser({
            ext_order_id: trackingInfo.externalOrderId,
            username: trackingInfo.customerName,
            merchantName: trackingInfo.merchantName,
          });
          this.checkFinishedLoading();
          const trackingInterval = this.getTrackingInterval();
          this.setRefreshInterval(trackingInterval);
          //  this.setIframeTokenData(this.authService.getIdToken()||'', trackingInfo.orderId);
          // this.injectDataToIframe();
        },
        error: (err) => {
          this.errorState = true;
          this.loadingState = false;
          Sentry.captureException(err);
          if (this.trackingInterval) {
            clearInterval(this.trackingInterval);
            this.trackingInterval = undefined;
          }
        },
      });
  }

  private setRefreshInterval(trackingInterval: number) {
    if (trackingInterval > 0) {
      this.trackingInterval = setInterval(() => {
        this.trackingService
          .trackingGet()
          .pipe(last())
          .subscribe({
            next: (trackingInfo) => {
              if (this.handleReRoute(trackingInfo)) {
                return;
              }
              this.orderTrackingInfo = trackingInfo;
              const newTrackingInterval = this.getTrackingInterval();
              if (newTrackingInterval != trackingInterval) {
                clearInterval(this.trackingInterval);
                this.trackingInterval = undefined;
                this.setRefreshInterval(newTrackingInterval);
              }
            },
            error: (err) => {
              Sentry.captureException(err);
            },
          });
      }, trackingInterval);
    } else if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
      this.trackingInterval = undefined;
    }
  }

  getBrandingInfo() {
    this.brandingService
      .merchantBrandingGet()
      .pipe(last())
      .subscribe({
        next: (brandingInfo) => {
          this.brandingAndDetails = brandingInfo;
          this.checkFinishedLoading();
        },
        error: (err) => {
          this.errorState = true;
          this.loadingState = false;
          Sentry.captureException(err);
        },
      });
  }

  checkFinishedLoading() {
    if (this.orderTrackingInfo && this.brandingAndDetails) {
      this.loadingState = false;
      this.initializeChat();
      this.route.queryParams.subscribe((params) => {
        this.isAdmin = !!params['admin'];
        localStorage.setItem(
          'gtmUser',
          JSON.stringify({
            identifier: this.orderTrackingInfo.customerId,
            customerId: this.orderTrackingInfo.customerId,
            customerName:
              this.orderTrackingInfo.tokenType === TokenTypeEnum.Sender
                ? this.orderTrackingInfo.senderName
                : this.orderTrackingInfo.customerName,
            merchantId: this.brandingAndDetails.merchantDetails.id,
            isRecipient: this.orderTrackingInfo.tokenType === TokenTypeEnum.Customer,
            isAdmin: this.isAdmin,
          }),
        );
        this.route.url.subscribe((url) => {
          this.gtmTracking.registerView('/' + url[0].path, 'tracking', {
            orderId: this.orderTrackingInfo.orderId,
            isGift: this.orderTrackingInfo.isGift,
          });
        });
      });
    }
  }

  /**
   * When the order has been re-routed, the tracking response carries a redirect
   * directive pointing at the successor order's own token. Navigate there with a
   * full page load so the app re-authenticates on the new token. Returns true
   * when a redirect was performed so callers stop processing the response.
   */
  private handleReRoute(trackingInfo: TrackingInfo): boolean {
    if (trackingInfo.redirect?.token) {
      this.navigateToToken(trackingInfo.redirect.token);
      return true;
    }
    return false;
  }

  navigateToToken(token: string) {
    // Only the token path segment changes; keep the current query string and hash
    // so tracking/UTM/lang params carry over to the successor order's page.
    window.location.href = `/${token}${window.location.search}${window.location.hash}`;
  }

  private getTrackingInterval() {
    if (!TrackingFinalStatuses.includes(this.orderTrackingInfo.customerTrackingStatus)) {
      if (
        TrackingLiveStatuses.includes(this.orderTrackingInfo.customerTrackingStatus) &&
        this.orderTrackingInfo.trackingInfo.dropoffEta
      ) {
        return 5000;
      }
      return 60000;
    }
    return -1;
  }

  protected readonly event = event;
  isDevEnvironment = !environment.production;
}
